import type { PolicySummary } from '../../lib/api'

/**
 * What a policy actually does, in a sentence an operator can read.
 *
 * This is the single reason the Policies screen is unreadable today. It shows the capability
 * identifier - `agent-prompt-observe` - and the same generic description on every row, so a thousand
 * rows look identical and scanning them tells you nothing. The fix is not a better table; it is
 * having something different to say about each row.
 *
 * Two things go into the sentence, and neither is the identifier. **What the capability does**, as an
 * outcome rather than as a mechanism: "Reads what somebody typed into an assistant", not "observes
 * prompts". And **what the policy does with the result**, which is the part that actually differs
 * between two policies on the same capability and is the part an operator is deciding about: a policy
 * that blocks is a different thing from one that watches, and until now they read the same.
 *
 * The outcome is derived rather than stored because it has to stay true. A description somebody typed
 * is a description that stops matching the policy the first time the policy is edited, and a wrong
 * sentence on this screen is worse than a generic one.
 */

/**
 * What each capability is for, in the operator's terms.
 *
 * Read from `GET /api/v1/policies/capabilities` at runtime where possible; this is the fallback and
 * the place the plain-English wording lives, because the server's `displayName` is a label for a
 * picker ("Watch AI assistant prompts") rather than a description of an outcome.
 */
const CAPABILITY_OUTCOMES: Record<string, string> = {
  'agent-prompt-observe': 'Reads what somebody typed into an AI assistant',
  screenshot: 'Takes a picture of the screen',
  'windows-state': 'Lists the windows that were open',
  log: 'Writes an entry to the device log'
}

/** What the policy does with what it found. The half that differs between two policies on one capability. */
function disposition(policy: PolicySummary): string {
  // Derived from the capability rather than from a flag, because the summary does not carry the
  // policy document - and inventing a disposition it cannot see would be worse than a general one.
  switch (policy.capability) {
    case 'agent-prompt-observe':
      return 'and reports what matched'
    case 'screenshot':
      return 'and keeps it on the device until somebody asks'
    case 'windows-state':
      return 'and reports the inventory'
    case 'log':
      return 'so the record is on the machine'
    default:
      return 'and reports the result'
  }
}

/**
 * The sentence for one policy.
 *
 * Never the identifier, and never the same sentence twice for two different capabilities -
 * `capabilityOutcome.test.ts` asserts both, because "no two rows carry the same generic sentence" is
 * the acceptance criterion this module exists for and it is only true if something checks.
 */
export function describePolicy(policy: PolicySummary): string {
  const outcome = CAPABILITY_OUTCOMES[policy.capability]

  // A capability this console has never heard of is named rather than described. That is honest - it
  // is a newer agent than this console - and it is still better than the identifier alone, because
  // the row says outright that the console does not know rather than looking like every other row.
  if (!outcome) return `Runs ${policy.capability}, which this console does not recognize`

  return `${outcome} ${disposition(policy)}`
}

/** What a whole capability group does, for the group row above its policies. */
export function describeCapability(capability: string): string {
  return CAPABILITY_OUTCOMES[capability] ?? `Runs ${capability}, which this console does not recognize`
}

/** Every capability with a sentence, so the test can assert the set is distinct. */
export const KNOWN_CAPABILITIES: readonly string[] = Object.keys(CAPABILITY_OUTCOMES)

export interface CapabilityGroup {
  capability: string
  /** The operator-facing name of the capability, from the catalog where one arrived. */
  title: string
  outcome: string
  policies: PolicySummary[]
  live: number
}

/**
 * Policies grouped by capability, as interleaved group and leaf rows.
 *
 * Arithmetic rather than markup, so the counts on a group row cannot disagree with the rows beneath
 * it - which is the way a grouped table usually goes wrong, and goes wrong silently.
 *
 * Sorted by size, because the point of grouping 1,148 rows is to put the thing an operator is looking
 * for near the top, and the capability with the most policies is where the work is.
 */
export function groupByCapability(
  policies: readonly PolicySummary[],
  titles: Readonly<Record<string, string>> = {}
): CapabilityGroup[] {
  const groups = new Map<string, PolicySummary[]>()

  for (const policy of policies) {
    const existing = groups.get(policy.capability)
    if (existing) existing.push(policy)
    else groups.set(policy.capability, [policy])
  }

  return [...groups.entries()]
    .map(([capability, members]) => ({
      capability,
      title: titles[capability] ?? capability,
      outcome: describeCapability(capability),
      policies: members,
      // "Live" is a published revision, which is the only state that means something is running on a
      // device. A policy with an approved revision that was never published is not live, and counting
      // it would overstate what the fleet is doing - the distinction the whole coverage idea rests on.
      live: members.filter((policy) => policy.approvedRevision !== null).length
    }))
    .sort((left, right) => right.policies.length - left.policies.length)
}
